/**
 * ZonesConsolidatedMap
 * Vista consolidada de todos los polígonos/círculos de zonas + ancla por sucursal.
 * (Xiomara QA20-07 #2 + Darling QA17-07 #1)
 *
 * - Renderiza TODAS las zonas activas en un solo mapa
 * - Cada sucursal tiene un color distinto (paleta corporativa)
 * - Marker de "ancla" en el centroide de cada sucursal
 * - Click sobre un polígono → callback onZoneClick(zone)
 * - Panel lateral opcional con lista de sucursales/zonas (renderizado externo)
 */
import { useMemo, useEffect } from "react";
import { MapContainer, TileLayer, Marker, Polygon, Circle, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin, Building2 } from "lucide-react";

const DEFAULT_CENTER = [-0.1807, -78.4678]; // Quito, EC

// Ícono para técnicos (pin con inicial del nombre)
const techIcon = (status, letter) => {
  const bg = status === "active" ? "#059669" : status === "stale" ? "#F59E0B" : "#9CA3AF";
  const pulse = status === "active" ? "box-shadow:0 0 0 4px rgba(5,150,105,.4);" : "";
  return L.divIcon({
    className: "",
    html: `<div style="background:${bg};color:#fff;width:22px;height:22px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-weight:bold;font-size:10px;border:2px solid #fff;${pulse}font-family:'Chivo',sans-serif">${letter}</div>`,
    iconSize: [22, 22],
    iconAnchor: [11, 11],
    popupAnchor: [0, -14],
  });
};

// Paleta corporativa (10 colores rotativos para sucursales)
const BRANCH_PALETTE = [
  "#0A3747", // corporate navy
  "#E0E621", // brand lime
  "#059669", // emerald 600
  "#F59E0B", // amber 500
  "#DC2626", // red 600
  "#7C3AED", // violet 600
  "#0891B2", // cyan 600
  "#DB2777", // pink 600
  "#65A30D", // lime 600
  "#EA580C", // orange 600
];

const colorForBranch = (idx) => BRANCH_PALETTE[idx % BRANCH_PALETTE.length];

// Ícono ancla corporativo por sucursal (usa color de la sucursal)
const anchorIcon = (color, letter) =>
  L.divIcon({
    className: "",
    html: `<div style="background:${color};color:#fff;width:28px;height:28px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);display:flex;align-items:center;justify-content:center;font-weight:bold;font-size:12px;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.5);font-family:'Chivo',sans-serif"><span style="transform:rotate(45deg)">${letter}</span></div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 28],
    popupAnchor: [0, -28],
  });

// Normaliza cualquier formato de coordenadas soportado por el backend
// Soporta: geometry.coords ó geometry.coordinates (arrays [lat,lon] u objetos {lat,lng})
const readPolygonCoords = (zone) => {
  const raw = zone?.geometry?.coords || zone?.geometry?.coordinates;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((c) => {
      if (Array.isArray(c) && c.length >= 2) return [Number(c[0]), Number(c[1])];
      if (c && typeof c === "object") return [Number(c.lat), Number(c.lng ?? c.lon)];
      return null;
    })
    .filter((p) => p && Number.isFinite(p[0]) && Number.isFinite(p[1]));
};

const readCircleCenter = (zone) => {
  const c = zone?.geometry?.center;
  if (!c) return null;
  if (Array.isArray(c) && c.length >= 2) return [Number(c[0]), Number(c[1])];
  if (typeof c === "object") return [Number(c.lat), Number(c.lng ?? c.lon)];
  return null;
};

// Calcula el centroide de una zona (polígono o círculo)
const centroidOfZone = (zone) => {
  if (zone.geometry_type === "polygon") {
    const coords = readPolygonCoords(zone);
    if (coords.length === 0) return null;
    const lat = coords.reduce((a, c) => a + c[0], 0) / coords.length;
    const lng = coords.reduce((a, c) => a + c[1], 0) / coords.length;
    return [lat, lng];
  }
  if (zone.geometry_type === "circle") {
    return readCircleCenter(zone);
  }
  return null;
};

// Bounds calc para fit-to-content
function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (points.length === 0) return;
    if (points.length === 1) {
      map.setView(points[0], 11);
    } else {
      const bounds = L.latLngBounds(points);
      map.fitBounds(bounds, { padding: [40, 40] });
    }
  }, [points, map]);
  return null;
}

export default function ZonesConsolidatedMap({
  branches = [],
  zones = [],
  height = 560,
  onZoneClick,
  onBranchClick,
  selectedBranchId = null,
  selectedZoneId = null,
  technicians = [],
}) {
  // Mapear branchId → color + índice + letra
  const branchStyle = useMemo(() => {
    const map = {};
    branches.forEach((b, idx) => {
      const letter = (b.name || b.code || "?").trim().charAt(0).toUpperCase();
      map[b.id] = {
        color: colorForBranch(idx),
        letter,
        name: b.name,
        code: b.code,
      };
    });
    map[""] = { color: "#6B7280", letter: "?", name: "Sin sucursal", code: "orfana" };
    return map;
  }, [branches]);

  // Puntos para fit bounds: centroides de las zonas VISIBLES (respetando filtro por sucursal)
  const fitPoints = useMemo(() => {
    const pts = [];
    zones.forEach((z) => {
      if (z.active === false) return;
      if (selectedBranchId && z.branch_id !== selectedBranchId) return;
      const c = centroidOfZone(z);
      if (c) pts.push(c);
      // Agrega también los vértices del polígono al bounds para mejor encuadre
      if (z.geometry_type === "polygon") {
        const coords = readPolygonCoords(z);
        coords.forEach((p) => pts.push(p));
      }
    });
    return pts;
  }, [zones, selectedBranchId]);

  // Anclas por sucursal: centroide promedio de sus zonas
  const branchAnchors = useMemo(() => {
    const anchors = [];
    branches.forEach((b) => {
      const bZones = zones.filter((z) => z.branch_id === b.id && z.active);
      const centroids = bZones.map(centroidOfZone).filter(Boolean);
      if (centroids.length === 0) return;
      const lat = centroids.reduce((a, c) => a + c[0], 0) / centroids.length;
      const lng = centroids.reduce((a, c) => a + c[1], 0) / centroids.length;
      const style = branchStyle[b.id] || { color: "#6B7280", letter: "?" };
      anchors.push({ branch: b, position: [lat, lng], color: style.color, letter: style.letter, zoneCount: bZones.length });
    });
    return anchors;
  }, [branches, zones, branchStyle]);

  // Filtro visual: si hay una sucursal seleccionada, atenúa el resto
  const isFiltered = !!selectedBranchId;

  return (
    <div className="relative rounded-lg overflow-hidden border border-gray-200 shadow-sm" style={{ height }} data-testid="zones-consolidated-map">
      <MapContainer center={DEFAULT_CENTER} zoom={7} style={{ height: "100%", width: "100%" }}>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <FitBounds points={fitPoints} />

        {/* Polígonos y círculos de zonas · v1.9.12: filtrar por sucursal + formato coords/coordinates */}
        {zones.filter((z) => z.active !== false).filter((z) => !isFiltered || z.branch_id === selectedBranchId).map((zone) => {
          const style = branchStyle[zone.branch_id || ""] || { color: "#6B7280" };
          const highlighted = selectedZoneId === zone.id;
          const opacity = highlighted ? 0.6 : 0.35;
          const weight = highlighted ? 4 : 2;

          if (zone.geometry_type === "polygon") {
            const positions = readPolygonCoords(zone);
            if (positions.length < 3) return null;
            return (
              <Polygon
                key={zone.id}
                positions={positions}
                pathOptions={{ color: style.color, weight, fillColor: style.color, fillOpacity: opacity }}
                eventHandlers={{ click: () => onZoneClick?.(zone) }}
              >
                <Popup>
                  <div className="text-xs" style={{ fontFamily: "Chivo, sans-serif" }}>
                    <div className="font-bold" style={{ color: style.color }}>{zone.name}</div>
                    <div className="text-gray-600 font-mono text-[10px] mt-0.5">{zone.code}</div>
                    {style.name && <div className="text-gray-500 mt-1">Sucursal: <b>{style.name}</b></div>}
                    <div className="text-gray-500">Polígono · {positions.length} vértices</div>
                    <button onClick={() => onZoneClick?.(zone)} className="mt-2 w-full px-2 py-1 bg-emerald-600 text-white text-[10px] rounded hover:bg-emerald-700">
                      Editar zona
                    </button>
                  </div>
                </Popup>
              </Polygon>
            );
          }
          if (zone.geometry_type === "circle") {
            const center = readCircleCenter(zone);
            if (!center) return null;
            const r = (zone.geometry.radius_km || 2.5) * 1000;
            return (
              <Circle
                key={zone.id}
                center={center}
                radius={r}
                pathOptions={{ color: style.color, weight, fillColor: style.color, fillOpacity: opacity }}
                eventHandlers={{ click: () => onZoneClick?.(zone) }}
              >
                <Popup>
                  <div className="text-xs" style={{ fontFamily: "Chivo, sans-serif" }}>
                    <div className="font-bold" style={{ color: style.color }}>{zone.name}</div>
                    <div className="text-gray-600 font-mono text-[10px] mt-0.5">{zone.code}</div>
                    {style.name && <div className="text-gray-500 mt-1">Sucursal: <b>{style.name}</b></div>}
                    <div className="text-gray-500">Círculo · radio {zone.geometry.radius_km || 2.5} km</div>
                    <button onClick={() => onZoneClick?.(zone)} className="mt-2 w-full px-2 py-1 bg-emerald-600 text-white text-[10px] rounded hover:bg-emerald-700">
                      Editar zona
                    </button>
                  </div>
                </Popup>
              </Circle>
            );
          }
          return null;
        })}

        {/* Anclas de sucursales (marker centroide) · filtrado por sucursal seleccionada */}
        {branchAnchors.filter((a) => !isFiltered || a.branch.id === selectedBranchId).map((a) => (
          <Marker
            key={a.branch.id}
            position={a.position}
            icon={anchorIcon(a.color, a.letter)}
            eventHandlers={{ click: () => onBranchClick?.(a.branch) }}
          >
            <Popup>
              <div className="text-xs" style={{ fontFamily: "Chivo, sans-serif" }}>
                <div className="font-bold flex items-center gap-1" style={{ color: a.color }}>
                  <Building2 size={12} /> {a.branch.name}
                </div>
                <div className="text-gray-600 font-mono text-[10px] mt-0.5">{a.branch.code}</div>
                {a.branch.province && <div className="text-gray-500 mt-1">{a.branch.province}</div>}
                <div className="text-gray-700 mt-1"><b>{a.zoneCount}</b> zonas · Ancla en centroide</div>
              </div>
            </Popup>
          </Marker>
        ))}

        {/* v10 · Pines de técnicos en tiempo real */}
        {technicians.filter((t) => t.last_ping?.lat && t.last_ping?.lon).map((t) => {
          const letter = (t.full_name || t.email || "?").charAt(0).toUpperCase();
          return (
            <Marker
              key={t.id}
              position={[t.last_ping.lat, t.last_ping.lon]}
              icon={techIcon(t.status, letter)}
            >
              <Popup>
                <div className="text-xs" style={{ fontFamily: "Chivo, sans-serif" }}>
                  <div className="font-bold text-emerald-700 flex items-center gap-1">
                    <MapPin size={11} /> {t.full_name || t.email}
                  </div>
                  {t.branch_name && <div className="text-gray-500 mt-0.5">Sucursal: <b>{t.branch_name}</b></div>}
                  <div className="text-gray-500 mt-0.5">
                    Estado: <span className={`font-semibold ${t.status === "active" ? "text-emerald-600" : t.status === "stale" ? "text-amber-600" : "text-gray-400"}`}>
                      {t.status === "active" ? "Activo" : t.status === "stale" ? "Inactivo" : "Offline"}
                    </span>
                  </div>
                  {t.last_ping?.age_seconds != null && (
                    <div className="text-gray-400 font-mono text-[10px] mt-0.5">
                      Último ping: {t.last_ping.age_seconds < 60 ? `${t.last_ping.age_seconds}s` : `${Math.round(t.last_ping.age_seconds / 60)}min`}
                    </div>
                  )}
                  {t.next_order && (
                    <div className="mt-1 text-[10px] text-gray-600 border-t pt-1">
                      Próxima OT: <b>{t.next_order.code || t.next_order.odoo_task_id}</b>
                      {t.next_order.is_late && <span className="ml-1 text-red-600 font-bold">RETRASADA</span>}
                    </div>
                  )}
                </div>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>

      {/* Leyenda flotante */}
      {branches.length > 0 && (
        <div className="absolute top-2 right-2 bg-white/95 backdrop-blur border border-gray-200 rounded-md shadow-md p-2 max-w-[220px] max-h-[280px] overflow-y-auto z-[500]" data-testid="map-legend">
          <div className="text-[10px] font-semibold text-gray-700 uppercase tracking-wider mb-1.5 flex items-center gap-1">
            <MapPin size={10} /> Sucursales
          </div>
          <div className="space-y-1">
            {branches.map((b, idx) => {
              const c = colorForBranch(idx);
              const zoneCount = zones.filter((z) => z.branch_id === b.id && z.active).length;
              const active = selectedBranchId === b.id;
              return (
                <button
                  key={b.id}
                  onClick={() => onBranchClick?.(b)}
                  className={`w-full flex items-center gap-1.5 text-left text-[10px] px-1 py-0.5 rounded hover:bg-gray-100 ${active ? "bg-emerald-50 ring-1 ring-emerald-500" : ""}`}
                  data-testid={`legend-branch-${b.code}`}
                >
                  <span className="w-3 h-3 rounded-sm flex-shrink-0" style={{ background: c }} />
                  <span className="flex-1 truncate">{b.name}</span>
                  <span className="text-gray-500 font-mono">{zoneCount}</span>
                </button>
              );
            })}
          </div>
          {/* Indicador de técnicos live */}
          {technicians.length > 0 && (
            <div className="mt-2 pt-2 border-t">
              <div className="text-[10px] font-semibold text-gray-700 uppercase tracking-wider mb-1 flex items-center gap-1">
                <MapPin size={10} className="text-emerald-600" /> Técnicos
              </div>
              <div className="flex items-center gap-3 text-[10px] text-gray-600">
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" />{technicians.filter((t) => t.status === "active").length} activos</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500" />{technicians.filter((t) => t.status === "stale").length} inactivos</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-gray-400" />{technicians.filter((t) => t.status === "offline").length} offline</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
